import { ReactElement, useEffect, useState } from "react";
import { Link as RouterLink, To } from "react-router-dom";
import {
  Button,
  Center,
  Heading,
  Box,
  SimpleGrid,
  Flex,
  Icon,
  Link,
  Text,
  ButtonProps,
} from "@chakra-ui/react";
import { ReactSVG } from "react-svg";
import { IoChevronForward, IoLogOut, IoOpenOutline } from "react-icons/io5";
import { IconType } from "react-icons";
import { useTranslation } from "react-i18next";
import { useAtom } from "jotai";
import {
  employeeInfoAtom,
  employeeContractAtom,
  footerIndexAtom,
} from "../store";
import { BasicPageLayout } from "../components/templates/BasicPageLayout";
import { BottomNavLayout } from "../components/templates/BottomNavLayout";
import { LogoutConfirmDialog } from "../components/molecules/LogoutConfirmDialog";

import personCircleImageUrl from "/src/assets/images/base/IoPersonCircle.svg";
import { setSVGFillColor } from "../utils/svgHelper";
import { useModifiedTranslation } from "../hooks/useModifiedTranslation";
import { isDebug } from "./calibrationV2";
import { NumericDataText } from "../components/atoms/NumericDataText";
import { useColorToken } from "../hooks/useColorToken";

// レイアウトパーツ：ヘッダータイトル
function PageTitle(): ReactElement {
  const t = useModifiedTranslation();
  return (
    <Heading size="md" textAlign="center" color="text.main_text_lv1">
      {t("Setting.title")}
    </Heading>
  );
}

type SettingButtonProps = ButtonProps & {
  title: string;
  icon: IconType;
  isDebug?: boolean;
  testId?: string;
} & (
    | { to: To; href?: never; onClick?: never }
    | { href: string; to?: never; onClick?: never }
    | { onClick: () => void; to?: never; href?: never }
  );

function SettingButton(props: Readonly<SettingButtonProps>): ReactElement {
  const buttonVariant = props.isDebug ? "btn_debug" : "btn_primary";
  const isInternal = !!props.to;
  const isExternal = !!props.href;

  const button = (
    <Button
      variant={buttonVariant}
      w="full"
      onClick={props.onClick}
      data-testid={props.testId}
    >
      <Box textAlign="left" ml={{ base: 3, md: 4 }}>
        {props.title}
      </Box>
      <Icon fontSize="3xl" ml="auto" mr={{ base: 3, md: 4 }} as={props.icon} />
    </Button>
  );

  if (isInternal) {
    return (
      <Link as={RouterLink} to={props.to}>
        {button}
      </Link>
    );
  }

  if (isExternal) {
    return (
      <Link href={props.href} isExternal>
        {button}
      </Link>
    );
  }

  return <>{button}</>;
}

/*
 * 設定画面
 */
export function Setting(): ReactElement {
  const { i18n } = useTranslation();
  const t = useModifiedTranslation();

  const [, setFooterIndex] = useAtom(footerIndexAtom);
  const [isLogoutOpen, setIsLogoutOpen] = useState(false);

  useEffect(() => {
    setFooterIndex(3);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const themeLv1 = useColorToken("primary.theme_lv1");

  // ログインユーザー情報
  const [employeeInfo] = useAtom(employeeInfoAtom);

  const lang = i18n.resolvedLanguage;

  // 外部リンク情報
  const [employeeContract] = useAtom(employeeContractAtom);

  return (
    <BottomNavLayout>
      <BasicPageLayout>
        <Center h={10} position="relative">
          <PageTitle />
        </Center>
        {/* 設定 ユーザーID ユーザー名 */}
        <Box mt={{ base: 6, md: 8 }} color="common_text.dark">
          <Flex
            bg="primary.bg_lv1"
            minHeight={20}
            rounded="2xl"
            justify="left"
            align="center"
            backgroundColor="primary.theme_lv3"
          >
            <Box
              fontSize="4xl"
              ml={{ base: 4, md: 5 }}
              color="text.main_text_lv1"
              textAlign="left"
            >
              <ReactSVG
                src={personCircleImageUrl}
                beforeInjection={(svg) => {
                  svg.setAttribute("width", "40");
                  svg.setAttribute("height", "40");
                  setSVGFillColor(svg, ".cls-1", "white");
                  setSVGFillColor(svg, ".cls-2", themeLv1);
                }}
              />
            </Box>
            <Box ml={{ base: 4, md: 5 }} flex="1" minW="0">
              <Text fontSize="md" fontWeight="bold" noOfLines={1}>
                {employeeInfo.employee_id}
              </Text>
              <Text fontSize="md" fontWeight="bold" noOfLines={1}>
                {employeeInfo.employee_name}
              </Text>
            </Box>
          </Flex>

          <SimpleGrid columns={{ base: 1, md: 1 }} spacingY={5} mt={5}>
            <SettingButton
              title={t("Setting.itemTitlePasswordChange")}
              icon={IoChevronForward}
              to={`/${lang}/setting/password`}
              testId="setting-password"
            />
          </SimpleGrid>

          <SimpleGrid columns={{ base: 1, md: 1 }} spacingY={5} mt={10}>
            <Box color="text.main_text_lv1">{t("Setting.subTitleApp")}</Box>

            {/* 古いキャリブレーション画面 */}
            <SettingButton
              title={t("Setting.itemTitleCalibration")}
              icon={IoChevronForward}
              to={`/${lang}/calibration`}
              testId="setting-calibration"
            />
            {/* 新しいキャリブレーション画面 */}
            {isDebug && (
              <SettingButton
                title={t("Setting.itemTitleCalibration2")}
                icon={IoChevronForward}
                to={`/${lang}/calibration_new`}
                isDebug
              />
            )}
            <SettingButton
              title={t("Setting.itemTitleColorLayout")}
              icon={IoChevronForward}
              to={`/${lang}/setting/color_layout`}
              testId="setting-color-layout"
            />
            <SettingButton
              title={t("Setting.itemTitlePhrase")}
              icon={IoChevronForward}
              to={`/${lang}/setting/phrase`}
              testId="setting-phrase"
            />
          </SimpleGrid>

          <SimpleGrid columns={{ base: 1, md: 1 }} mt={10} spacingY={5}>
            <Box color="text.main_text_lv1">{t("Setting.subTitleOther")}</Box>
            <SettingButton
              title={t("Setting.itemTitlePrivacyPolicy")}
              icon={IoOpenOutline}
              href={employeeContract.privacy_policy}
            />
            <SettingButton
              title={t("Setting.itemTitleTermsOfService")}
              icon={IoOpenOutline}
              href={employeeContract.terms_of_service}
            />
            <SettingButton
              title={t("Home.logout")}
              icon={IoLogOut}
              onClick={() => setIsLogoutOpen(true)}
              testId="setting-logout"
            />
          </SimpleGrid>

          <LogoutConfirmDialog
            isOpen={isLogoutOpen}
            onClose={() => setIsLogoutOpen(false)}
          />

          <NumericDataText
            color="text.main_text_lv1"
            mt={10}
            textAlign="center"
            fontWeight="thin"
          >
            {t("Setting.version")} {__APP_VERSION__}({__GIT_DESCRIBE_REVISION__}
            )
          </NumericDataText>
        </Box>
      </BasicPageLayout>
    </BottomNavLayout>
  );
}
